import { v4 as uuidv4 } from 'uuid';
import { useCallback, useState } from 'react';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { connect } from 'react-redux';

import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IKnmErknmActRemark } from 'APIPackage/KnmModule/interfaces/IKnmErknmActRemark';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { Button } from 'UIPackage/MainModule/components/Button';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { IKnmErknmActRemarkForm } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmActRemarkForm';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { getActRemarksBlockId } from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { parseDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_MINUTE_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { TextInput } from 'UIPackage/MainModule/components/TextInput';

import { SignatureBlock } from './SignatureBlock';
import styles from './ErknmActRemarks.module.scss';
import { ReactQuillEditorField } from '../../../../../../UIPackage/MainModule/components/ReactQuillEditorField';
import { checkHasErrorInCollapseBlock } from '../../../../../../functions/checkHasErrorInCollapseBlock';
import { IRootState } from '../../../../../../CorePackage/MainModule/interfaces/IRootState';
import { getUserRoleIsSupervisor } from '../../../../../../AuthPackage/MainModule/selectors/authSelectors';

interface IErknmActRemarksProps {
  id: string;
  name: string;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  isPm?: boolean;
  isDecision?: boolean;
  isDisabledByDirty?: boolean;
  holdingDateTime?: string;
}

interface IErknmActRemarksStoreProps {
  isSupervisor: boolean;
}

const ErknmActRemarksWithoutStore = ({
  id,
  name,
  formRef,
  isDecision,
  isPm,
  isDisabledByDirty = false,
  holdingDateTime,
  isSupervisor,
}: IErknmActRemarksProps & IErknmActRemarksStoreProps) => {
  const [knmCollapseOpened, setKnmCollapseOpened] = useState<{ [key: string]: boolean }>({});
  const remove = formRef?.current.mutators.remove;
  const push = formRef?.current.mutators.push;

  const isDisabled = isDisabledByDirty || !isSupervisor;

  const addRemark = useCallback(() => {
    if (push) {
      const addingEvent: IKnmErknmActRemarkForm = {
        guid: uuidv4(),
        isNew: true,
        text: undefined,
        signerFullName: undefined,
        signerPosition: undefined,
        date: undefined,
        signatureInfo: undefined,
      };

      push(`${name}.remarks`, addingEvent);
    }
  }, [formRef, push]);

  /**
   * Обработчик удаления замечания
   */
  const onRemoveRemark = useCallback(
    (index: number) => {
      remove && remove(`${name}.remarks`, index);
    },
    [formRef],
  );

  const minDateByHoldingDateTime = parseDate({
    date: holdingDateTime || '',
    parseFormat: DATE_FORMAT_LONG_MINUTE_FNS,
  });

  return (
    <FieldBlock
      label={'Замечания контролируемого лица'}
      className={styles.GroupMarginBottom}
      button={
        <Button
          className="shared-btn-add"
          text="Добавить"
          variant="link"
          color="primary"
          type="button"
          disabled={isDisabled}
          onClick={addRemark}
        />
      }
    >
      <FieldArray name={`${name}.remarks`}>
        {({ fields }: FieldArrayRenderProps<IKnmErknmActRemark, HTMLElement>) => {
          if (!fields.length) {
            return null;
          }

          return fields.map((fieldName, sIndex) => {
            const fieldValue: IKnmErknmActRemark = fields.value?.[sIndex];
            const itemId = getActRemarksBlockId(id, fieldValue.guid || '');

            const hasError = checkHasErrorInCollapseBlock(formRef, fieldName);

            return (
              <KnmCollapse
                id={itemId}
                key={fieldValue.guid}
                theme="fields"
                title={`Замечание №${sIndex + 1}`}
                opened={true}
                isNew={!!fieldValue.fromEpgu}
                isNewTitle="ЕПГУ"
                onClose={fieldValue.isNew && !isDisabled ? () => onRemoveRemark(sIndex) : undefined}
                headerPostfix={fieldValue.isNew && <div className={styles.ActStatus}>Черновик</div>}
                changeOpenedCallback={e =>
                  setKnmCollapseOpened(state => ({
                    ...state,
                    [fieldName]: e,
                  }))
                }
                error={hasError && knmCollapseOpened[fieldName]}
              >
                <div className={styles.Wrapper}>
                  <CheckboxField
                    className={styles.CheckboxRefusal}
                    name={`${fieldName}.refusedToFamiliarize`}
                    label="Отказ от ознакомления"
                    theme="system"
                    type="checkbox"
                    disabled={!!fieldValue.fromEpgu || !fieldValue.isNew || isDisabled}
                  />
                  {!fieldValue.refusedToFamiliarize && (
                    <>
                      <FieldBlock
                        label={getTitleFieldWithRedStar({
                          label: 'Текст замечания',
                          field: `${fieldName}.textReactQuillEditor`,
                          isPm,
                          isDecision,
                          isKnm: !isPm && !isDecision,
                        })}
                        placementLabel="left"
                        color="black"
                        className={styles.FieldBlock}
                        id={`${itemId}.textReactQuillEditor`}
                      >
                        <ReactQuillEditorField
                          name={`${fieldName}.textReactQuillEditor`}
                          id={`${fieldName}.textReactQuillEditor`}
                          handleCustomChange={(e: string) => {
                            // @ts-ignore
                            formRef?.current?.change(`${fieldName}.text` as never, e);
                          }}
                          disabled={!fieldValue.isNew || isDisabled}
                        />
                      </FieldBlock>
                      {fieldValue.fromEpgu ? (
                        <FieldBlock
                          label={'ФИО и должность лица, оставившего замечание'}
                          color="black"
                          placementLabel="left"
                          className={styles.FieldBlock}
                        >
                          <TextInput
                            disabled={true}
                            value={`${fieldValue.signerFullName} - ${fieldValue.signerPosition}`}
                          />
                        </FieldBlock>
                      ) : (
                        <>
                          <FieldBlock
                            label={getTitleFieldWithRedStar({
                              label: 'ФИО лица, оставившего замечание',
                              field: `${fieldName}.signerFullName`,
                              isPm,
                              isDecision,
                              isKnm: !isPm && !isDecision,
                            })}
                            color="black"
                            placementLabel="left"
                            id={`${itemId}.signerFullName`}
                            className={styles.FieldBlock}
                          >
                            <TextInputField
                              name={`${fieldName}.signerFullName`}
                              disabled={!fieldValue.isNew || isDisabled}
                            />
                          </FieldBlock>
                          <FieldBlock
                            label={getTitleFieldWithRedStar({
                              label: 'Должность лица, оставившего замечание',
                              field: `${fieldName}.signerPosition`,
                              isPm,
                              isDecision,
                              isKnm: !isPm && !isDecision,
                            })}
                            color="black"
                            placementLabel="left"
                            id={`${itemId}.signerPosition`}
                            className={styles.FieldBlock}
                          >
                            <TextInputField
                              name={`${fieldName}.signerPosition`}
                              disabled={!fieldValue.isNew || isDisabled}
                            />
                          </FieldBlock>
                        </>
                      )}
                      <FieldBlock
                        label={getTitleFieldWithRedStar({
                          label: 'Дата',
                          field: `${fieldName}.date`,
                          isPm,
                          isDecision,
                          isKnm: !isPm && !isDecision,
                        })}
                        id={`${itemId}.date`}
                        placementLabel="left"
                        color="black"
                        className={styles.FieldBlock}
                      >
                        <DatePickerField
                          name={`${fieldName}.date`}
                          disabled={!fieldValue.isNew || isDisabled}
                          minDate={minDateByHoldingDateTime}
                        />
                      </FieldBlock>
                      {fieldValue.signatureInfo && <SignatureBlock signatureInfo={fieldValue.signatureInfo} />}
                    </>
                  )}
                </div>
              </KnmCollapse>
            );
          });
        }}
      </FieldArray>
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState): IErknmActRemarksStoreProps => ({
  isSupervisor: getUserRoleIsSupervisor(state),
});

export const ErknmActRemarks = connect(mapStateToProps)(ErknmActRemarksWithoutStore);
